import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Download,
  ArrowLeft,
  FileWarning,
  MapPin,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function BatchDetailPage() {
  const { batchId } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comunaFilter, setComunaFilter] = useState("ALL");
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get(`/batches/${batchId}`);
      setBatch(data);
    } catch {
      toast.error("Error al cargar el lote");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [batchId]);

  const comunas = useMemo(() => {
    if (!batch) return [];
    const set = new Set();
    for (const d of batch.documents || []) {
      if (d.delivery_comuna) set.add(d.delivery_comuna.toUpperCase());
    }
    return Array.from(set).sort();
  }, [batch]);

  const filteredDocs = useMemo(() => {
    if (!batch) return [];
    if (comunaFilter === "ALL") return batch.documents;
    return batch.documents.filter(
      (d) => (d.delivery_comuna || "").toUpperCase() === comunaFilter
    );
  }, [batch, comunaFilter]);

  const downloadSap = async () => {
    try {
      const url =
        comunaFilter === "ALL"
          ? `/batches/${batchId}/sap`
          : `/batches/${batchId}/sap?comuna=${encodeURIComponent(comunaFilter)}`;
      const res = await api.get(url, { responseType: "blob" });
      const objUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = objUrl;
      const suffix = comunaFilter === "ALL" ? "TODAS" : comunaFilter.replace(/\s+/g, "_");
      a.download = `SAP_${(batch.descripcion || "EXIS").replace(/\s+/g, "_")}_${suffix}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      toast.success("SAP descargado");
    } catch {
      toast.error("Error al descargar SAP");
    }
  };

  const updateItem = async (docId, itemId, patch) => {
    try {
      const { data } = await api.patch(
        `/batches/${batchId}/documents/${docId}/items/${itemId}`,
        patch
      );
      setBatch(data);
      toast.success("Actualizado");
    } catch {
      toast.error("Error al actualizar");
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="p-10 text-slate-500">Cargando lote...</div>
      </AppShell>
    );
  }

  if (!batch) {
    return (
      <AppShell>
        <div className="p-10 text-slate-500">Lote no encontrado.</div>
      </AppShell>
    );
  }

  const totalItems = batch.documents.reduce(
    (s, d) => s + (d.items?.length || 0),
    0
  );

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-6 md:p-10">
        <Link
          to="/batches"
          data-testid="batch-back"
          className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-slate-500 hover:text-[#D95A40] transition-colors mb-4"
        >
          <ArrowLeft className="w-3 h-3" /> {isAdmin ? "Volver a envíos" : "Volver a productos"}
        </Link>

        <PageHeader
          eyebrow={`${isAdmin ? "Envío" : "Producto"} · ${new Date(batch.created_at).toLocaleString("es-CL")}`}
          title={
            (batch.products_summary && batch.products_summary[0]) ||
            batch.descripcion ||
            "Detalle del envío"
          }
          description={(() => {
            const rest = (batch.products_summary?.length || 0) - 1;
            const parts = [
              `Responsable: ${batch.responsable || "—"}`,
              `Cliente: ${batch.id_cliente || "—"}`,
            ];
            if (rest > 0)
              parts.push(`+${rest} producto${rest > 1 ? "s" : ""} más`);
            return parts.join(" · ");
          })()}
          actions={
            <div className="flex items-center gap-3">
              <div className="w-56">
                <Select value={comunaFilter} onValueChange={setComunaFilter}>
                  <SelectTrigger
                    data-testid="batch-comuna-filter"
                    className="border-slate-300"
                  >
                    <MapPin className="w-4 h-4 mr-2 text-slate-500" />
                    <SelectValue placeholder="Filtrar por comuna" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="ALL">Todas las comunas</SelectItem>
                    {comunas.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <button
                onClick={downloadSap}
                data-testid="batch-download-sap-btn"
                className={`flex items-center gap-2 px-5 py-3 bg-[#D95A40] hover:bg-[#c94d33] text-white font-bold uppercase tracking-[0.15em] text-xs transition-colors ${isAdmin ? "" : "hidden"}`}
              >
                <Download className="w-4 h-4" />
                Descargar SAP
              </button>
            </div>
          }
        />

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Stat label="Documentos" value={batch.documents.length} />
          <Stat label="Productos" value={batch.products_summary?.length || 0} />
          <Stat label="Comunas" value={comunas.length} />
          <Stat
            label="Errores"
            value={batch.errors?.length || 0}
            highlight={batch.errors?.length > 0}
          />
        </div>

        {/* Errors */}
        {batch.errors?.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 mb-6 p-4">
            <div className="flex items-center gap-2 mb-2 text-amber-800">
              <FileWarning className="w-4 h-4" />
              <div className="font-bold uppercase text-xs tracking-[0.15em]">
                Archivos con errores
              </div>
            </div>
            <ul className="text-xs text-amber-800 space-y-1 list-disc pl-6">
              {batch.errors.map((e, i) => (
                <li key={i}>
                  <span className="font-mono">{e.file}</span>: {e.error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Documents list */}
        <div className="bg-white border border-slate-200 divide-y divide-slate-100">
          {filteredDocs.map((d) => (
            <div key={d.id} data-testid={`doc-row-${d.id}`}>
              <button
                onClick={() =>
                  setExpanded((s) => ({ ...s, [d.id]: !s[d.id] }))
                }
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors text-left"
              >
                <div className="flex items-center gap-4 min-w-0">
                  {expanded[d.id] ? (
                    <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[#0B1B3D] text-white">
                        DTE {d.tipo_dte}
                      </span>
                      <span className="font-display font-bold text-[#0B1B3D]">
                        Folio {d.folio}
                      </span>
                      <span className="text-xs text-slate-500">
                        {d.tipo_dte_nombre}
                      </span>
                    </div>
                    <div className="text-sm text-slate-700 mt-1 truncate">
                      {d.rzn_soc_receptor}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {d.delivery_direccion} —{" "}
                      <span className="font-bold text-[#D95A40]">
                        {d.delivery_comuna}
                      </span>
                      {d.region && (
                        <span className="text-slate-400">
                          {" "}
                          · Región {d.region}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right shrink-0 ml-4">
                  <div className="font-display font-bold text-lg tabular-nums text-[#0B1B3D]">
                    ${d.mnt_total?.toLocaleString("es-CL") || 0}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">
                    {d.items?.length || 0} items
                  </div>
                </div>
              </button>

              {expanded[d.id] && (
                <div className="bg-slate-50 border-t border-slate-100 px-5 py-4">
                  <div className="grid md:grid-cols-3 gap-4 mb-4 text-xs">
                    <InfoField
                      label="Emisor"
                      value={`${d.rzn_soc_emisor} (${d.rut_emisor})`}
                    />
                    <InfoField
                      label="Receptor"
                      value={`${d.rzn_soc_receptor} (${d.rut_receptor})`}
                    />
                    <InfoField label="Fecha emisión" value={d.fch_emis} />
                    <InfoField label="Correo receptor" value={d.correo_receptor} />
                    <InfoField label="Contacto" value={d.contacto || d.telefono} />
                    <InfoField
                      label="Referencia OC"
                      value={
                        d.references?.[0]
                          ? `${d.references[0].folio_ref} — ${d.references[0].razon_ref}`
                          : "—"
                      }
                    />
                  </div>

                  <div className="overflow-x-auto bg-white border border-slate-200">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-100">
                        <tr>
                          <Th2>#</Th2>
                          <Th2>Producto</Th2>
                          <Th2>Código SAP</Th2>
                          <Th2>Lote</Th2>
                          <Th2 className="text-right">Unidades</Th2>
                          <Th2 className="text-right">Bultos</Th2>
                          <Th2>Punto entrega</Th2>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {d.items.map((it) => (
                          <tr
                            key={it._item_id}
                            data-testid={`item-row-${it._item_id}`}
                            className="hover:bg-slate-50"
                          >
                            <td className="px-3 py-2 tabular-nums text-slate-500">
                              {it.nro_lin_det}
                            </td>
                            <td className="px-3 py-2 text-[#0B1B3D]">
                              {it.nmb_item}
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                defaultValue={it.codigo_producto || ""}
                                onBlur={(e) =>
                                  e.target.value !== (it.codigo_producto || "") &&
                                  updateItem(d.id, it._item_id, {
                                    codigo_producto: e.target.value,
                                  })
                                }
                                data-testid={`item-codigo-${it._item_id}`}
                                className="w-full px-2 py-1 border border-slate-200 focus:border-[#D95A40] focus:outline-none text-xs"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                defaultValue={it.lote || ""}
                                onBlur={(e) => {
                                  const v = e.target.value.trim();
                                  if (v !== (it.lote || ""))
                                    updateItem(d.id, it._item_id, { lote: v });
                                }}
                                data-testid={`item-lote-${it._item_id}`}
                                placeholder="—"
                                className="w-28 px-2 py-1 border border-slate-200 focus:border-[#D95A40] focus:outline-none text-xs font-mono uppercase"
                              />
                            </td>
                            <td className="px-3 py-2 text-right">
                              <input
                                type="number"
                                step="0.01"
                                defaultValue={it.unidades}
                                onBlur={(e) => {
                                  const v = parseFloat(e.target.value);
                                  if (v !== it.unidades)
                                    updateItem(d.id, it._item_id, { unidades: v });
                                }}
                                data-testid={`item-unidades-${it._item_id}`}
                                className="w-24 text-right px-2 py-1 border border-slate-200 focus:border-[#D95A40] focus:outline-none text-xs tabular-nums"
                              />
                            </td>
                            <td className="px-3 py-2 text-right">
                              <input
                                type="number"
                                step="0.01"
                                defaultValue={it.bultos}
                                onBlur={(e) => {
                                  const v = parseFloat(e.target.value);
                                  if (v !== it.bultos)
                                    updateItem(d.id, it._item_id, { bultos: v });
                                }}
                                data-testid={`item-bultos-${it._item_id}`}
                                className="w-20 text-right px-2 py-1 border border-slate-200 focus:border-[#D95A40] focus:outline-none text-xs tabular-nums"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                defaultValue={it.id_punto_entrega}
                                onBlur={(e) =>
                                  e.target.value !== it.id_punto_entrega &&
                                  updateItem(d.id, it._item_id, {
                                    id_punto_entrega: e.target.value,
                                  })
                                }
                                data-testid={`item-punto-${it._item_id}`}
                                className="w-24 px-2 py-1 border border-slate-200 focus:border-[#D95A40] focus:outline-none text-xs"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ))}
          {filteredDocs.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-500">
              Sin documentos para el filtro seleccionado.
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div
      className={`bg-white border p-4 ${
        highlight ? "border-amber-300 bg-amber-50" : "border-slate-200"
      }`}
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">
        {label}
      </div>
      <div className="font-display font-black text-3xl text-[#0B1B3D] tabular-nums leading-none">
        {value}
      </div>
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">
        {label}
      </div>
      <div className="text-slate-800">{value || "—"}</div>
    </div>
  );
}

function Th2({ children, className = "" }) {
  return (
    <th
      className={`px-3 py-2 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-slate-600 ${className}`}
    >
      {children}
    </th>
  );
}
